// src/services/api.js
import axios from 'axios';

// 운영(배포) 시: REACT_APP_API_URL='' → baseURL = '/api/v1' (same-origin)
//   gateon.kr / www.gateon.kr 어느 호스트로 들어와도 동일 작동 (구 ticket.imotto.co.kr 는 301 redirect)
// 로컬 dev: REACT_APP_API_URL=http://localhost:8000 → 백엔드를 별 포트로 띄우는 환경 대응.
const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 토큰 키 상수
const ADMIN_TOKEN_KEY = 'accessToken';
const ADMIN_REFRESH_KEY = 'refreshToken';
const USER_TOKEN_KEY = 'userAccessToken';
const USER_REFRESH_KEY = 'userRefreshToken';

// ─────────────────────────────────────────────────────────
// 경로 분류
// ─────────────────────────────────────────────────────────

// 관리자 토큰을 써야 하는 경로 (백엔드 get_current_user/get_staff/... 의존 엔드포인트)
const ADMIN_PREFIXES = [
  '/admin/', '/auth/', '/users/admin/', '/platform/', '/admins', '/managers',
  '/entrances/', '/events/all', '/events/by-id/', '/invite/scan', '/files/', '/tickets/',
];
// 이벤트 쓰기(POST/PUT/DELETE /events/...)도 관리자 — 단, 공개 하위 리소스(주문/찜/Q&A/리뷰)는 제외
const EVENT_PUBLIC_SUBRESOURCE = /^\/events\/[^/]+\/(orders|favorite|qas|reviews|notices|time-slots)/;

const isAdminPath = (url, method) => {
  if (ADMIN_PREFIXES.some((p) => url.startsWith(p))) return true;
  if (/^\/orders\/\d+\/tickets/.test(url)) return true; // 주문별 티켓 조회 (관리자 전용)
  if (/^\/payments\/cancel\//.test(url)) return true;
  if (url.startsWith('/events') && method && method !== 'get' && !EVENT_PUBLIC_SUBRESOURCE.test(url)) return true;
  return false;
};

// 로그인/갱신/가입 등 자체적으로 401 을 의미있게 돌려주는 요청 — 인터셉터가 가로채면 안 됨
const AUTH_ENDPOINTS = [
  '/auth/login', '/auth/refresh', '/users/login', '/users/refresh', '/users/register',
  '/invite/login', '/social-login', '/users/password-reset/', '/auth/logout', '/users/logout',
];
const isAuthEndpoint = (url) => AUTH_ENDPOINTS.some((p) => url.startsWith(p));

const pathOf = (config) => {
  // baseURL 이 붙은 절대 URL 이 들어와도 경로만 비교
  const url = config.url || '';
  const idx = url.indexOf('/api/v1');
  return idx >= 0 ? url.slice(idx + '/api/v1'.length) : url;
};

// 요청 인터셉터 - 토큰 추가
api.interceptors.request.use(
  (config) => {
    // 이미 Authorization 헤더가 설정되어 있으면 건드리지 않음 (예: 초대권 업체 토큰)
    const existingAuth = config.headers.get ? config.headers.get('Authorization') : config.headers.Authorization;
    if (existingAuth) {
      return config;
    }

    const url = pathOf(config);
    if (isAdminPath(url, (config.method || 'get').toLowerCase())) {
      const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      if (adminToken) config.headers.Authorization = `Bearer ${adminToken}`;
      config._authKind = 'admin';
      return config;
    }

    // 그 외(회원/공개) 경로는 회원 토큰만 — 관리자 토큰을 회원 API 에 보내지 않음
    const userToken = localStorage.getItem(USER_TOKEN_KEY);
    if (userToken) {
      config.headers.Authorization = `Bearer ${userToken}`;
      config._authKind = 'user';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─────────────────────────────────────────────────────────
// 토큰 갱신 (single-flight) — 동시 401 이 와도 refresh 는 종류별 1회
// ─────────────────────────────────────────────────────────
const refreshing = { admin: null, user: null };

const refreshTokens = (kind) => {
  if (refreshing[kind]) return refreshing[kind];
  const refreshKey = kind === 'admin' ? ADMIN_REFRESH_KEY : USER_REFRESH_KEY;
  const tokenKey = kind === 'admin' ? ADMIN_TOKEN_KEY : USER_TOKEN_KEY;
  const endpoint = kind === 'admin' ? '/auth/refresh' : '/users/refresh';
  const refreshToken = localStorage.getItem(refreshKey);

  refreshing[kind] = (async () => {
    if (!refreshToken) throw new Error('no refresh token');
    // 보안: refresh_token 은 body 로 전달 (URL 노출 방지)
    const response = await axios.post(`${API_URL}/api/v1${endpoint}`, { refresh_token: refreshToken });
    const { access_token, refresh_token } = response.data;
    localStorage.setItem(tokenKey, access_token);
    if (refresh_token) localStorage.setItem(refreshKey, refresh_token);
    return access_token;
  })().finally(() => {
    // 진행 중인 갱신에 합류한 요청들은 이미 이 promise 를 보유 — 완료 즉시 해제해 다음 만료 때 새로 갱신
    refreshing[kind] = null;
  });
  return refreshing[kind];
};

const clearSession = (kind) => {
  if (kind === 'admin') {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_REFRESH_KEY);
  } else {
    localStorage.removeItem(USER_TOKEN_KEY);
    localStorage.removeItem(USER_REFRESH_KEY);
    localStorage.removeItem('userData');
  }
};

// 보호된 화면에서만 로그인으로 보냄. 공개 화면(행사 랜딩, 결제 결과 등)은 로그아웃 상태로 계속 진행.
const redirectToLoginIfProtected = (kind) => {
  const here = window.location.pathname;
  const back = encodeURIComponent(here + window.location.search);
  if (kind === 'admin') {
    if (here.startsWith('/super-admin')) window.location.href = '/super-admin/login';
    else if (here.startsWith('/admin')) window.location.href = '/admin/login';
  } else if (here.startsWith('/mypage')) {
    window.location.href = `/login?redirect=${back}`;
  }
};

// 응답 인터셉터 - 토큰 갱신
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const url = pathOf(originalRequest);

    if (error.response?.status !== 401 || originalRequest._retry || isAuthEndpoint(url)) {
      return Promise.reject(error);
    }

    // 초대권 업체 토큰 등 호출부가 직접 넣은 토큰 → 호출부가 처리
    const kind = originalRequest._authKind;
    if (!kind) return Promise.reject(error);

    originalRequest._retry = true;
    try {
      const newToken = await refreshTokens(kind);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      clearSession(kind);
      redirectToLoginIfProtected(kind);
      return Promise.reject(error);
    }
  }
);

// 테스트용 내부 함수 노출
export const __test__ = { isAdminPath, isAuthEndpoint };

export default api;
